// ── Beta Registration Entry Page (TPT96) ──
//
// Public landing page for `#/register` (optional `?code=` query param) —
// structural twin of pages/invite.js: public (no checkAuth()), paints
// straight into #app via teardownLayout(), previews/validates via a silent
// apiFetch, then hands off. Unlike invite.js, the hand-off target is the
// EXISTING signup wizard in pages/login.js, not an in-page accept button —
// this page's only job is turning a registration code into a validated
// email and seeding that wizard (startBetaSignup()), never a fourth step
// bolted onto login.js's own signupStep state machine (which already has a
// 'code' step meaning something else — the 6-digit email-verification code).
//
// Release redirect (TPT533): with no `?code=`, the page asks GET /auth/beta/phase
// first; in `release` (open signup) there is no code to ask for, so it goes
// straight to `#/signup`. Any other phase, or a failed lookup, keeps the code
// form — the decision is registerEntryAction() in lib/signup-form.js.
//
// POST /auth/beta/validate is a read-only lookup — it does NOT consume the
// code. Consumption happens once account creation actually completes
// (POST /auth/signup/complete, see auth.js's signupComplete()).

import { apiFetch } from '../api.js';
import { verifySession } from '../auth.js';
import { navigate, getQueryParams, setQueryParams } from '../router.js';
import { teardownLayout } from '../layout.js';
import { esc } from '../ui.js';
import { normalizeBetaCodeInput, betaCodeErrorText, registerEntryAction } from '../lib/signup-form.js';
import { startBetaSignup } from './login.js';
import { brandLockup } from '../lib/brand.js';

// Same default as api/src/lib/beta-codes.js's marketingSiteUrl() — this page
// has no server config to read, so the fallback is hardcoded to match.
const MARKETING_SITE_URL = 'https://tipatask.com';

function renderShell(bodyHTML) {
  const app = document.getElementById('app');
  app.innerHTML = `<div class="login-container">${brandLockup()}<div class="auth-card">${bodyHTML}</div></div>`;
  return app;
}

function renderInvalid(message) {
  renderShell(`
    <p>${esc(message)}</p>
    <a href="${MARKETING_SITE_URL}" class="btn btn-ghost" style="width:100%;">Request beta access</a>
    <div class="login-switch">
      Have a project invitation instead? <a href="#/login">Create account</a>
    </div>`);
}

async function validateAndHandOff(code) {
  renderShell('<p>Checking your code…</p>');
  try {
    const res = await apiFetch('/auth/beta/validate', {
      method: 'POST',
      body: JSON.stringify({ code }),
      _silent: true,
    });
    // Scrub the code out of the URL/history before navigating away — it's a
    // bearer secret, no reason to leave it sitting in a shareable link once
    // it's been read.
    setQueryParams(new URLSearchParams());
    // TPT103 — a shared (team) code's email belongs to whoever originally
    // applied, not to this visitor — don't pre-fill/confirm a stranger's
    // address for a teammate. login.js's renderSignupEmailStep() only shows
    // its "confirmed for X" notice when signupEmail is actually set.
    startBetaSignup({ code, email: res.shared ? '' : res.email });
    navigate('/login');
  } catch (err) {
    renderInvalid(betaCodeErrorText(err));
  }
}

function renderCodeForm() {
  const app = renderShell(`
    <p>Enter your beta registration code</p>
    <form class="login-form" id="register-code-form">
      <div class="form-group">
        <label class="form-label" for="register-code">Registration code</label>
        <input class="form-input" id="register-code" autocomplete="off" autocapitalize="off" spellcheck="false" autofocus required>
      </div>
      <div class="login-error" id="register-error"></div>
      <button type="submit" class="btn btn-primary" id="register-submit-btn" style="width:100%;">Continue</button>
    </form>
    <div class="login-switch">
      Don't have a code? <a href="${MARKETING_SITE_URL}">Request beta access</a><br>
      Have a project invitation instead? <a href="#/login">Create account</a>
    </div>`);

  const form = app.querySelector('#register-code-form');
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = app.querySelector('#register-submit-btn');
    const errEl = app.querySelector('#register-error');
    const raw = app.querySelector('#register-code').value;
    const normalized = normalizeBetaCodeInput(raw);
    if (!normalized) {
      errEl.textContent = 'Enter your registration code';
      return;
    }
    errEl.textContent = '';
    btn.disabled = true;
    btn.textContent = 'Checking…';
    try {
      const res = await apiFetch('/auth/beta/validate', {
        method: 'POST',
        body: JSON.stringify({ code: normalized }),
        _silent: true,
      });
      // TPT103 — see validateAndHandOff()'s identical comment above.
      startBetaSignup({ code: normalized, email: res.shared ? '' : res.email });
      navigate('/login');
    } catch (err) {
      errEl.textContent = betaCodeErrorText(err);
      btn.disabled = false;
      btn.textContent = 'Continue';
    }
  });
}

export async function registerPage() {
  teardownLayout();

  if (await verifySession()) {
    navigate('/');
    return;
  }

  const code = getQueryParams().get('code');
  if (code) {
    await validateAndHandOff(code);
    return;
  }
  if (registerEntryAction({ code, phase: await fetchBetaPhase() }) === 'signup') {
    navigate('/signup');
    return;
  }
  renderCodeForm();
}

// The API's live beta phase, or null when the lookup fails (an older API
// without the route, a network error) — null keeps the code form.
async function fetchBetaPhase() {
  try {
    const res = await apiFetch('/auth/beta/phase', { _silent: true });
    return res && typeof res.phase === 'string' ? res.phase : null;
  } catch {
    return null;
  }
}
